import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Alert, Box, Button, Paper, Skeleton, Stack, Typography } from '@mui/material';

import CollapsibleSection from './CollapsibleSection';
import GuestConsole from './guests/GuestConsole';
import GuestFilterBar from './guests/GuestFilterBar';
import GuestRecordEditor from './guests/GuestRecordEditor';
import GuestSummary from './guests/GuestSummary';
import GuestTable, {
  type GuestColumnGroup,
  type GuestRoster,
  type RowSaveState,
} from './guests/GuestTable';
import GuestSelectionBar from './guests/GuestSelectionBar';
import RosterConsole from './guests/RosterConsole';
import SendInviteDialog, { joinHostDisplayNames } from './guests/SendInviteDialog';
import GuestTagDialogs from './guests/GuestTagDialogs';
import { useGuestTags } from './guests/useGuestTags';
import {
  createTrackerSection,
  fetchTrackerBoard,
  fetchTrackerExport,
  importTrackerRows,
  patchTrackerGuest,
  sendGuestInvite,
  type InviteCompose,
  type TrackerBoard,
  trackerFailureMessage,
  type TrackerFailure,
  type TrackerGuest,
  type TrackerImportRow,
  type TrackerPatch,
} from '../../services/tracker-api-client';
import { guestNameList, nameListNotice, type NameListScope } from '../../lib/guestNameList';
import { EMPTY_GUEST_FILTER, filterGuests, type GuestFilter } from '../../lib/guestFilter';
import {
  allVisibleState,
  bulkTarget,
  pruneSelection,
  toggleAllVisible,
  toggleSelection,
} from '../../lib/guestSelection';
import {
  AUTO_TAG_KIND,
  TAGS_UNAVAILABLE_NOTE,
  tagCounts,
  tagsAvailable,
  type CreateTagKind,
} from '../../lib/guestTags';
import { applyOptimisticPatch, invertPatch } from '../../lib/trackerPatch';
import { distinctStayingAt } from '../../lib/trackerPresentation';
import { downloadBlob } from '../../lib/download';
import { groupPublicUrl } from '../../services/groups-api-client';
import type { InviteeImportPreview, InviteeImportResult } from '../../lib/inviteeImport';
import type {
  GroupDetail,
  GroupInvitee,
  GroupInviteeInput,
  SendGroupInvitesRequest,
  SendGroupInvitesResponse,
} from '../../types/groups';

/** Collaboration MVP: poll while the tab is visible, no presence avatars. */
const REFRESH_INTERVAL_MS = 30_000;
const SAVED_TICK_MS = 2_000;

type LoadState =
  | { status: 'loading' }
  | { status: 'unauthorized' }
  | { status: 'not_host' }
  | { status: 'error'; message: string }
  | { status: 'ready'; board: TrackerBoard };

const failureMessage = trackerFailureMessage;

/** A contact edit lands on `group_invitees`, which the `['group', id]` cache holds. */
function touchesRoster(patch: TrackerPatch): boolean {
  return 'contactEmail' in patch || 'contactPhone' in patch;
}

function TrackerLoading() {
  const placeholder = {
    borderRadius: 'var(--tsb-radius-md)',
    bgcolor: 'var(--tsb-bg-elevated)',
  } as const;
  return (
    <Stack spacing={2} role="status" aria-label="Loading the guest list">
      <Skeleton variant="rounded" height={72} sx={placeholder} />
      <Skeleton variant="rounded" height={64} sx={placeholder} />
      <Skeleton variant="rounded" height={420} sx={placeholder} />
    </Stack>
  );
}

function TerminalPanel({
  title,
  description,
  severity,
  action,
}: {
  title: string;
  description: string;
  severity: 'info' | 'warning' | 'error';
  action?: ReactNode;
}) {
  return (
    <Paper sx={{ p: 3, borderRadius: 3 }}>
      <Typography component="h2" variant="h6" fontWeight={800}>{title}</Typography>
      <Alert severity={severity} action={action} sx={{ mt: 2 }}>{description}</Alert>
    </Paper>
  );
}

/** Both column groups start on: the point of the merge is seeing a guest whole. */
const DEFAULT_COLUMN_GROUPS: GuestColumnGroup[] = ['roster', 'logistics'];

export interface GuestsTabProps {
  group: GroupDetail;
  /** Invalidates the `['group', id]` query the rest of the page reads. */
  onRosterChanged: () => void;
  /*
    The roster writes stay on the page's react-query lane rather than moving
    into the board's hand-rolled one: they mutate `group_invitees`, which the
    rest of the page reads, so they have to invalidate that cache. GroupDetail
    already owns those mutations — they arrive here as props instead of being
    re-implemented against a second client.
  */
  onPreviewImport: (invitees: GroupInviteeInput[]) => Promise<InviteeImportPreview>;
  /**
   * Returns the write's result rather than void: it reports per-row failures
   * (a duplicate address, a row two others claimed) that the API deliberately
   * survives instead of failing the batch. Throwing that away meant a 137-row
   * import could skip rows and report nothing at all — success by silence, which
   * is the failure mode this whole path exists to remove.
   */
  onImportInvitees: (
    invitees: GroupInviteeInput[],
    rosterFingerprint: string,
  ) => Promise<InviteeImportResult>;
  onSendInvites: (body: SendGroupInvitesRequest) => Promise<SendGroupInvitesResponse>;
  onSegmentChange: (invitee: GroupInvitee, segment: string | null) => void;
  rosterSaving: boolean;
  sending: boolean;
  importError: string;
  sendError: string;
  sendResult?: SendGroupInvitesResponse;
}

/**
 * The host-facing guest tracker.
 *
 * This board deliberately does NOT use react-query, unlike every other write on
 * this page. Ordering here is enforced by hand: every write takes a ticket from
 * `writeSeq`, each row remembers its newest one in `latestWrite`, and a response
 * may only touch the board if it still holds that ticket. A background read is
 * dropped whole if any write started while it was in flight.
 *
 * That is what makes rolling a rejected edit back safe. Suppressing refetches
 * while `inFlight > 0` is not sufficient on its own — it is checked when a read
 * fires, not when it lands — so the ticket is the actual guarantee and the
 * in-flight count is only an optimisation. react-query's window-focus and
 * interval refetches would sit outside all of it, which is why the board stays
 * off it; the react-query lane is confined to writes touching `group_invitees`.
 */
export default function GuestsTab({
  group,
  onRosterChanged,
  onPreviewImport,
  onImportInvitees,
  onSendInvites,
  onSegmentChange,
  rosterSaving,
  sending,
  importError,
  sendError,
  sendResult,
}: GuestsTabProps) {
  const groupId = group.id;

  const [visibleGroups, setVisibleGroups] = useState<GuestColumnGroup[]>(DEFAULT_COLUMN_GROUPS);
  const [state, setState] = useState<LoadState>({ status: 'loading' });
  const [selectedInviteeId, setSelectedInviteeId] = useState<string | null>(null);
  const [rowState, setRowState] = useState<Record<string, RowSaveState>>({});
  const [invitingInviteeId, setInvitingInviteeId] = useState<string | null>(null);
  // The guest whose invite is being composed — Send invite opens the dialog;
  // the actual POST happens on the dialog's own Send.
  const [composingInviteeId, setComposingInviteeId] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [creatingSection, setCreatingSection] = useState(false);
  const [notice, setNotice] = useState<{ tone: 'success' | 'error'; message: string } | null>(null);
  /*
    Bulk tagging state. The ticked ids are the FALLBACK selector — the primary
    one is the filter, and `bulkTarget` decides which is in play. The anchor is
    an invitee id rather than a row index so a shift-range survives the list
    re-sorting underneath it.
  */
  const [selected, setSelected] = useState<ReadonlySet<string>>(() => new Set());
  const [anchorInviteeId, setAnchorInviteeId] = useState<string | null>(null);
  /** The guests the open palette is about, or null when it is closed. */
  const [paletteFor, setPaletteFor] = useState<string[] | null>(null);
  /** The kind its Create starts on: the intent of the button that opened it. */
  const [paletteKind, setPaletteKind] = useState<CreateTagKind>(AUTO_TAG_KIND);
  const [managingTags, setManagingTags] = useState(false);

  // The last patch per row, so "Retry" can resend exactly what failed.
  const lastPatch = useRef<Record<string, TrackerPatch>>({});
  const inFlight = useRef(0);
  const refreshTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  /*
    Every write takes a ticket, and each row remembers its newest one.

    Suppressing background reads while `inFlight > 0` is not enough on its own:
    that is checked when a read FIRES, never when it LANDS, so a GET issued
    during an idle moment can still resolve on top of an edit the host made a
    fraction of a second later. Same shape per row — the editor commits on blur,
    one field per request, so tabbing Email into Phone is two overlapping writes
    to one row by design. Without a ticket the LAST RESPONSE wins rather than the
    last request, which reverts a saved value on screen and lets a rejected early
    write roll back a later successful one.
  */
  const writeSeq = useRef(0);
  const latestWrite = useRef<Record<string, number>>({});

  const loadBoard = useCallback(async (id: string, background = false) => {
    const issuedAt = writeSeq.current;
    const result = await fetchTrackerBoard(id);
    if (result.ok) {
      // A write that started after this read holds newer truth than the snapshot
      // coming back. There is no way to merge the two, so the stale snapshot is
      // dropped whole and the next poll re-reads it.
      if (background && writeSeq.current !== issuedAt) return;
      setState({ status: 'ready', board: result.data });
      return;
    }
    // A failed background poll must not blow away a screen the host is using.
    if (background) return;
    if (result.failure.kind === 'unauthorized') setState({ status: 'unauthorized' });
    else if (result.failure.kind === 'not_host') setState({ status: 'not_host' });
    else setState({ status: 'error', message: failureMessage(result.failure) });
  }, []);

  useEffect(() => {
    void loadBoard(groupId);
  }, [groupId, loadBoard]);

  // Poll only while the tab is visible and nothing is mid-save, so a refetch
  // can never land on top of an edit that has not been acknowledged yet.
  useEffect(() => {
    if (state.status !== 'ready') return;
    const interval = setInterval(() => {
      if (document.visibilityState !== 'visible' || inFlight.current > 0) return;
      void loadBoard(groupId, true);
    }, REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [groupId, state.status, loadBoard]);

  useEffect(
    () => () => {
      if (refreshTimer.current) clearTimeout(refreshTimer.current);
    },
    [],
  );

  /** Debounced: the summary is server-computed, so it lags a burst of edits. */
  const scheduleSummaryRefresh = useCallback(() => {
    if (refreshTimer.current) clearTimeout(refreshTimer.current);
    refreshTimer.current = setTimeout(() => {
      if (inFlight.current > 0) return;
      void loadBoard(groupId, true);
    }, 1_500);
  }, [groupId, loadBoard]);

  const runPatch = useCallback(
    async (inviteeId: string, patch: TrackerPatch) => {
      const seq = (writeSeq.current += 1);
      latestWrite.current[inviteeId] = seq;
      lastPatch.current[inviteeId] = patch;
      inFlight.current += 1;
      setRowState((previous) => ({ ...previous, [inviteeId]: { status: 'saving' } }));

      /*
        Kept so a rejected edit can be taken back off the screen. Captured inside
        the updater because that is the only place with the authoritative latest
        state; it is a pure read of `previous`, so React re-invoking the updater
        just recomputes the same row.
      */
      let rollbackGuest: TrackerGuest | undefined;
      setState((previous) => {
        if (previous.status !== 'ready') return previous;
        rollbackGuest = previous.board.guests.find((guest) => guest.inviteeId === inviteeId);
        return {
          ...previous,
          board: {
            ...previous.board,
            guests: previous.board.guests.map((guest) =>
              guest.inviteeId === inviteeId ? applyOptimisticPatch(guest, patch) : guest,
            ),
          },
        };
      });

      const result = await patchTrackerGuest(groupId, inviteeId, patch);
      inFlight.current = Math.max(0, inFlight.current - 1);

      if (!result.ok) {
        /*
          Take the optimistic edit back off the screen. Without this a rejected
          change sat there looking saved until the next poll up to 30s later —
          and the poll skips while anything is in flight, so it could sit longer.
          On a 137-row table that is a host reading their own unsaved typing as
          the guest list.

          Only the fields this request changed are put back, so a sibling field
          saved on the same row meanwhile survives its neighbour's rejection.
        */
        if (rollbackGuest) {
          const undo = invertPatch(rollbackGuest, patch);
          setState((previous) =>
            previous.status === 'ready'
              ? {
                  ...previous,
                  board: {
                    ...previous.board,
                    guests: previous.board.guests.map((guest) =>
                      guest.inviteeId === inviteeId ? applyOptimisticPatch(guest, undo) : guest,
                    ),
                  },
                }
              : previous,
          );
        }
        // Left in place for Retry — this is the patch that failed, and it stays
        // recoverable even if a newer write for the row has since succeeded and
        // cleared its own.
        lastPatch.current[inviteeId] = patch;
        setRowState((previous) => ({
          ...previous,
          [inviteeId]: { status: 'error', message: failureMessage(result.failure) },
        }));
        if (result.failure.kind === 'unauthorized') setState({ status: 'unauthorized' });
        return;
      }

      /*
        A superseded write must not touch the row. Its response is a snapshot
        from before the newer edit, so writing it back would revert a value the
        host already saw save. The newest write owns the row, and its own
        response carries every earlier change the server has applied.
      */
      if (latestWrite.current[inviteeId] !== seq) return;

      delete lastPatch.current[inviteeId];
      const saved = result.data;
      setState((previous) =>
        previous.status === 'ready'
          ? {
              ...previous,
              board: {
                ...previous.board,
                guests: previous.board.guests.map((guest) =>
                  guest.inviteeId === inviteeId ? saved : guest,
                ),
              },
            }
          : previous,
      );
      setRowState((previous) => ({ ...previous, [inviteeId]: { status: 'saved' } }));
      setTimeout(() => {
        setRowState((previous) =>
          previous[inviteeId]?.status === 'saved'
            ? { ...previous, [inviteeId]: { status: 'idle' } }
            : previous,
        );
      }, SAVED_TICK_MS);
      scheduleSummaryRefresh();
      if (touchesRoster(patch)) onRosterChanged();
    },
    [groupId, scheduleSummaryRefresh, onRosterChanged],
  );

  const handleRetry = useCallback(
    (inviteeId: string) => {
      const patch = lastPatch.current[inviteeId];
      if (patch) void runPatch(inviteeId, patch);
    },
    [runPatch],
  );

  /** Splices an authoritative write's answer into the board already on screen. */
  const updateBoard = useCallback((mutate: (board: TrackerBoard) => TrackerBoard) => {
    setState((previous) =>
      previous.status === 'ready' ? { ...previous, board: mutate(previous.board) } : previous,
    );
  }, []);

  /*
    Tag writes take a ticket from the same counter the row patches use. A bulk
    apply that skipped it could be reverted on screen by a background GET that
    was already in flight when the host pressed Apply — the board drops any read
    whose ticket has been superseded, and that is the only thing making it safe.
  */
  const beginWrite = useCallback(() => {
    writeSeq.current += 1;
    inFlight.current += 1;
  }, []);
  const endWrite = useCallback(() => {
    inFlight.current = Math.max(0, inFlight.current - 1);
  }, []);

  const tagWrites = useGuestTags({
    groupId,
    updateBoard,
    beginWrite,
    endWrite,
    onNotice: setNotice,
  });

  /*
    Every path that opens a tag surface clears the last refusal on the way in.
    `tagWrites.error` is ONE string shared by every tag and link write, and it
    is only cleared when a dialog closes — so a refusal raised in the guest
    record ("Another host linked them already — …") was still there when the
    host next opened the palette, rendered as a red alert about an action they
    had not taken, attributed to one they had.
  */
  const clearTagError = tagWrites.clearError;
  const openTagPalette = useCallback(
    (inviteeIds: string[], intent: CreateTagKind = AUTO_TAG_KIND) => {
      clearTagError();
      setPaletteKind(intent);
      setPaletteFor(inviteeIds);
    },
    [clearTagError],
  );
  const openTagManager = useCallback(() => {
    clearTagError();
    setManagingTags(true);
  }, [clearTagError]);

  const handleImport = useCallback(
    async (rows: TrackerImportRow[]) => {
      setImporting(true);
      setNotice(null);
      const result = await importTrackerRows(groupId, rows);
      setImporting(false);
      if (!result.ok) {
        setNotice({ tone: 'error', message: failureMessage(result.failure) });
        return;
      }
      const { created, updated, linkedPlusOnes, errors } = result.data;
      setNotice({
        tone: errors.length ? 'error' : 'success',
        message:
          `${created} added, ${updated} updated, ${linkedPlusOnes} plus-${linkedPlusOnes === 1 ? 'one' : 'ones'} linked.` +
          (errors.length
            ? ` ${errors.length} row${errors.length === 1 ? '' : 's'} had a problem.`
            : ''),
      });
      await loadBoard(groupId);
      // The import writes group_invitees, so the rest of the page is now stale.
      onRosterChanged();
    },
    [groupId, loadBoard, onRosterChanged],
  );

  const handleExport = useCallback(async () => {
    setExporting(true);
    const result = await fetchTrackerExport(groupId);
    setExporting(false);
    if (!result.ok) {
      setNotice({ tone: 'error', message: failureMessage(result.failure) });
      return;
    }
    downloadBlob(`guest-list-${group.slug}.csv`, result.data);
  }, [groupId, group.slug]);

  const handleSendInvite = useCallback(
    async (inviteeId: string, compose?: InviteCompose) => {
      setInvitingInviteeId(inviteeId);
      setNotice(null);
      const result = await sendGuestInvite(groupId, inviteeId, compose);
      setInvitingInviteeId(null);
      if (result.ok) setComposingInviteeId(null);
      setNotice(
        result.ok
          ? { tone: 'success', message: 'Invite sent.' }
          : { tone: 'error', message: failureMessage(result.failure) },
      );
    },
    [groupId],
  );

  const handleCreateSection = useCallback(
    async (label: string) => {
      setCreatingSection(true);
      const result = await createTrackerSection(groupId, label);
      setCreatingSection(false);
      if (!result.ok) {
        setNotice({ tone: 'error', message: failureMessage(result.failure) });
        return;
      }
      await loadBoard(groupId);
    },
    [groupId, loadBoard],
  );

  const board = state.status === 'ready' ? state.board : null;

  const [filter, setFilter] = useState<GuestFilter>({ ...EMPTY_GUEST_FILTER });

  /*
    Tonight in the TRIP's timezone, not the browser's. A host opening Studio
    from a US phone at 23:00 Lisbon is already on tomorrow's date at home, and
    "here tonight" for the wrong night is worse than no filter at all.
  */
  const tonight = useMemo(
    () => new Intl.DateTimeFormat('en-CA', { timeZone: group.timeZone || undefined }).format(new Date()),
    [group.timeZone],
  );

  /*
    Every view of the roster reads this, and so does Copy names: a host who has
    narrowed to "going, Bairro Alto" and copies names expects those names, not
    all 137. One narrowing, honoured everywhere, is the whole point.
  */
  const visibleGuests = useMemo(
    () => filterGuests(board?.guests, filter),
    [board?.guests, filter],
  );

  /*
    Null means this API has no tag routes at all; `[]` means it has them and
    this trip has no tags yet. Everything downstream keys off that difference,
    and it is the whole reason none of the tag controls can render as a button
    that writes nowhere.
  */
  const vocabulary = tagsAvailable(board) ? board?.tags ?? [] : null;
  /*
    Memoised for its identity, not its cost. An API that sends no `links` key
    would otherwise hand the record tile a brand-new `[]` on every render, and
    the tile's own memos — which walk the whole roster — would rebuild on every
    keystroke in the search box above them.
  */
  const links = useMemo(() => board?.links ?? [], [board?.links]);
  /*
    Trip-wide, not filter-wide. "How many vegetarians" is a question about the
    trip, and a count that quietly changed with the search box is the wrong
    number to read out to a caterer.
  */
  const counts = useMemo(() => tagCounts(board?.guests ?? []), [board?.guests]);
  const target = useMemo(() => bulkTarget(visibleGuests, selected), [visibleGuests, selected]);

  /*
    A guest deleted in another tab leaves a tick nothing can clear, and the bulk
    apply would then send an id the API refuses — turning the whole batch into
    an error over somebody who is not there any more.
  */
  useEffect(() => {
    if (!board) return;
    setSelected((previous) => {
      const pruned = pruneSelection(previous, board.guests);
      return pruned.size === previous.size ? previous : pruned;
    });
  }, [board]);

  const paletteGuests = useMemo(() => {
    if (!paletteFor || !board) return [];
    const wanted = new Set(paletteFor);
    return board.guests.filter((guest) => wanted.has(guest.inviteeId));
  }, [paletteFor, board]);

  /*
    The seeded dietary and access vocabulary is inserted lazily, and this is the
    only thing that asks for it. The design puts those twenty-odd allergy, diet
    and mobility entries on a trip "on first use of the Health & access picker"
    rather than on every trip at creation — the picker is the palette, so
    opening it is the trigger. Without this the Health & access section of a
    fresh trip is empty, and a host types "GF" where the seeds would have given
    them gluten free with its critical flag already set.

    Not awaited, and not on the busy flag: the palette draws from the vocabulary
    already in hand and the seeds splice in when they land. `ensureHealthVocabulary`
    is once per trip, so the dependency churning on every open costs nothing.
  */
  const ensureHealthVocabulary = tagWrites.ensureHealthVocabulary;
  const tagsSupported = vocabulary !== null;
  useEffect(() => {
    if (paletteFor && tagsSupported) ensureHealthVocabulary();
  }, [paletteFor, tagsSupported, ensureHealthVocabulary]);

  /**
   * Names on the clipboard, from the roster already on screen.
   *
   * No fetch: the board is the same data the export endpoint would return, and
   * a host doing this is standing somewhere with one bar of signal handing a
   * list to a restaurant. It has to be instant and it has to work offline.
   */
  const handleCopyNames = useCallback(
    (scope: NameListScope) => {
      /*
        Tonight in the TRIP's timezone, not the browser's. A host who opens
        Studio from a US phone at 23:00 Lisbon is already on tomorrow's date at
        home, and a door list for the wrong night is worse than no door list.
      */
      const day = tonight;
      const list = guestNameList(visibleGuests, scope, day);
      if (list.count === 0) {
        setNotice({ tone: 'error', message: nameListNotice(list, scope) });
        return;
      }
      // Safari and every browser refuse the clipboard outside a user gesture and
      // on insecure origins. Saying so beats a menu that silently does nothing.
      void navigator.clipboard
        .writeText(list.text)
        .then(() => setNotice({ tone: 'success', message: nameListNotice(list, scope) }))
        .catch(() =>
          setNotice({
            tone: 'error',
            message: 'This browser would not let Studio reach the clipboard. Use Export CSV instead.',
          }),
        );
    },
    /*
      The filter, not the board. This closure reads `visibleGuests`, which is
      recomputed when the filter changes and NOT when the board does — so keyed
      on `board` alone it kept copying the list from before the host narrowed
      it. Copy names honours the filter by design; this is what makes that true
      at the moment the host presses it rather than at the last refetch.
    */
    [visibleGuests, tonight],
  );

  const roomBlocks = group.roomBlocks;
  const stayingAtOptions = useMemo(() => {
    if (!board) return [];
    // One case-insensitive fold over both sources. distinctStayingAt already
    // folds the typed values; re-splitting them with a case-sensitive Set is
    // what put "Tivoli" and "tivoli" in the list as two hotels.
    const seen = new Map<string, string>();
    for (const raw of [...distinctStayingAt(board.guests), ...roomBlocks.map((b) => b.hotelName)]) {
      const value = raw?.trim();
      if (!value) continue;
      const key = value.toLowerCase();
      if (!seen.has(key)) seen.set(key, value);
    }
    return [...seen.values()].sort((a, b) => a.localeCompare(b));
  }, [board, roomBlocks]);
  const selectedGuest =
    board?.guests.find((guest) => guest.inviteeId === selectedInviteeId) ?? null;

  const invitees = group.invitees;
  const slug = group.slug;
  /*
    Joins the roster half onto the board by invitee id. The two halves come from
    different reads and can disagree for a moment — a guest imported through the
    tracker is on the board before the group query refetches — so the table reads
    a missing entry as "not known yet" and renders a placeholder rather than
    assuming the roster row is absent.
  */
  const rosterByInviteeId = useMemo(() => {
    const map: Record<string, GuestRoster> = {};
    for (const invitee of invitees) {
      map[invitee.id] = {
        segment: invitee.segment ?? null,
        source: invitee.source,
        inviteUrl: groupPublicUrl(slug, invitee.inviteToken),
      };
    }
    return map;
  }, [invitees, slug]);

  const toggleGroup = useCallback((columnGroup: GuestColumnGroup) => {
    setVisibleGroups((previous) =>
      previous.includes(columnGroup)
        ? previous.filter((entry) => entry !== columnGroup)
        : [...previous, columnGroup],
    );
  }, []);

  const handleSegmentChange = useCallback(
    (inviteeId: string, segment: string | null) => {
      const invitee = invitees.find((entry) => entry.id === inviteeId);
      if (invitee) onSegmentChange(invitee, segment);
    },
    [invitees, onSegmentChange],
  );

  if (state.status === 'unauthorized') {
    // No sign-in button: the shared axios 401 interceptor has already cleared
    // the session and started the redirect to /login.
    return (
      <TerminalPanel
        severity="warning"
        title="Sign in to open the guest list"
        description="Your session expired. Taking you to sign in again."
      />
    );
  }

  if (state.status === 'not_host') {
    // 404 from the tracker means "not a host", never "no such trip" — the gate
    // answers 404 so it cannot confirm which group ids exist.
    return (
      <TerminalPanel
        severity="info"
        title="This one isn't yours to edit"
        description="The email you're signed in with isn't a host on this trip. Ask whoever set it up to add you as a host."
      />
    );
  }

  if (state.status === 'error') {
    return (
      <TerminalPanel
        severity="error"
        title="We couldn't load the guest list"
        description={state.message}
        action={
          <Button color="inherit" size="small" onClick={() => void loadBoard(groupId)}>
            Try again
          </Button>
        }
      />
    );
  }

  if (state.status === 'loading' || !board) return <TrackerLoading />;

  return (
    <Stack spacing={2}>
      <Box component="section" aria-labelledby="guest-tracker-heading">
        <Typography id="guest-tracker-heading" component="h2" variant="h6" fontWeight={800}>
          Guest list
        </Typography>
        <Typography color="text.secondary" variant="body2" sx={{ mt: 0.5, mb: 2 }}>
          Everyone on this trip and everything the hosts know about them.
          {' '}{board.guests.length} on the list.
        </Typography>
        <GuestSummary summary={board.summary} />
      </Box>

      {/*
        Folded away by default once there are guests to look at. These are
        setup tools — a host uses them at the start of a trip and then wants
        the list itself, which with 137 rows needs the screen. An empty list
        opens them, because then importing IS the task.
      */}
      <CollapsibleSection
        id="guests-roster"
        title="Invitees and invites"
        summary="Import invitees, send invites"
        defaultOpen={board.guests.length === 0}
      >
        <RosterConsole
          group={group}
          onPreviewImport={onPreviewImport}
          onImport={onImportInvitees}
          onSend={onSendInvites}
          saving={rosterSaving}
          sending={sending}
          error={importError}
          sendError={sendError}
          sendResult={sendResult}
        />
      </CollapsibleSection>

      <CollapsibleSection
        id="guests-console"
        title="Import, export and columns"
        summary="Paste a guest list, download the roster, choose which columns show"
        defaultOpen={board.guests.length === 0}
      >
        <GuestConsole
          onImport={handleImport}
          onExport={handleExport}
          onCopyNames={handleCopyNames}
          importing={importing}
          exporting={exporting}
          onDismissNotice={() => setNotice(null)}
          visibleGroups={visibleGroups}
          onToggleGroup={toggleGroup}
          tags={vocabulary}
        />
      </CollapsibleSection>

      {/*
        Beside the roster, not inside the import console: that panel is
        collapsed by default on every trip that has guests, so this — the only
        account a bulk apply gives of itself, including "Nothing needed
        changing." — used to render at height zero behind a folded accordion.
      */}
      {notice ? (
        <Alert
          severity={notice.tone === 'success' ? 'success' : 'warning'}
          role="status"
          onClose={() => setNotice(null)}
        >
          {notice.message}
        </Alert>
      ) : null}

      {board.guests.length === 0 ? (
        <Alert severity="info">
          Nobody on the list yet. Paste a guest list or import a Partiful export above.
        </Alert>
      ) : (
        <>
        {/*
          Said once, plainly, instead of drawing a tag row that filters nothing
          and a bulk button that writes nowhere. This codebase has shipped
          controls that silently did nothing before; the cost was a host
          believing work had been saved that never was.
        */}
        {vocabulary === null ? (
          <Alert severity="info">{TAGS_UNAVAILABLE_NOTE}</Alert>
        ) : null}
        <GuestFilterBar
          filter={filter}
          onChange={setFilter}
          hotels={stayingAtOptions}
          shown={visibleGuests.length}
          total={board.guests.length}
          tonight={tonight}
          tags={vocabulary}
          tagCounts={counts}
          bulkLabel={target.label}
          bulkDisabled={target.inviteeIds.length === 0}
          onBulkTag={() => openTagPalette(target.inviteeIds)}
        />
        {vocabulary ? (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button size="small" onClick={openTagManager}>
              Manage tags
            </Button>
          </Box>
        ) : null}
        <GuestTable
          guests={visibleGuests}
          roster={rosterByInviteeId}
          visibleGroups={visibleGroups}
          stayingAtOptions={stayingAtOptions}
          rowState={rowState}
          selectedInviteeId={selectedInviteeId}
          invitingInviteeId={invitingInviteeId}
          onSelect={setSelectedInviteeId}
          onPatch={(inviteeId, patch) => void runPatch(inviteeId, patch)}
          onRetry={handleRetry}
          onSendInvite={setComposingInviteeId}
          onSegmentChange={handleSegmentChange}
          segmentSaving={rosterSaving}
          vocabulary={vocabulary}
          selected={selected}
          allVisible={allVisibleState(selected, visibleGuests)}
          onToggleSelect={(inviteeId, shiftKey) => {
            setSelected((previous) =>
              toggleSelection(previous, visibleGuests, inviteeId, {
                extendFrom: shiftKey ? anchorInviteeId : null,
              }),
            );
            setAnchorInviteeId(inviteeId);
          }}
          onToggleAllVisible={() =>
            setSelected((previous) => toggleAllVisible(previous, visibleGuests))
          }
        />
        <GuestSelectionBar
          visible={visibleGuests}
          selected={selected}
          disabled={tagWrites.busy}
          onTag={() => openTagPalette([...selected])}
          onClear={() => {
            setSelected(new Set());
            setAnchorInviteeId(null);
          }}
        />
        </>
      )}

      {composingInviteeId ? (
        <SendInviteDialog
          guestName={
            board.guests.find((g) => g.inviteeId === composingInviteeId)?.fullName ?? 'this guest'
          }
          defaultSignedBy={joinHostDisplayNames(board.hosts)}
          sending={invitingInviteeId === composingInviteeId}
          onClose={() => setComposingInviteeId(null)}
          onSend={(compose) => void handleSendInvite(composingInviteeId, compose)}
        />
      ) : null}

      {selectedGuest ? (
        <GuestRecordEditor
          guest={selectedGuest}
          sections={board.sections}
          onPatch={(inviteeId, patch) => void runPatch(inviteeId, patch)}
          onClose={() => setSelectedInviteeId(null)}
          onCreateSection={(label) => void handleCreateSection(label)}
          creatingSection={creatingSection}
          vocabulary={vocabulary}
          links={links}
          roster={board.guests}
          tagBusy={tagWrites.busy}
          tagError={tagWrites.error}
          onDismissTagError={clearTagError}
          onOpenTagPalette={(intent) => openTagPalette([selectedGuest.inviteeId], intent)}
          /*
            Taking one tag off one guest is the same batch call as taking it off
            two hundred — a batch of one. There is deliberately no second write
            path for it: two ways to record one fact is how the two come to
            disagree.
          */
          onRemoveTag={(tagId) =>
            void tagWrites.assign(
              { tagIds: [tagId], mode: 'remove', note: '' },
              [selectedGuest.inviteeId],
            )
          }
          onCreateLink={(link) => tagWrites.createLink(selectedGuest.inviteeId, link)}
          onDeleteLink={(linkId) => void tagWrites.deleteLink(linkId)}
        />
      ) : null}

      {vocabulary ? (
        <GuestTagDialogs
          vocabulary={vocabulary}
          allGuests={board.guests}
          paletteFor={paletteFor}
          paletteKind={paletteKind}
          paletteGuests={paletteGuests}
          managingTags={managingTags}
          writes={tagWrites}
          onPaletteApplied={() => {
            setPaletteFor(null);
            // The ticks are consumed by the apply. Leaving them on invites a
            // second apply to the same people that the host did not intend.
            setSelected(new Set());
            setAnchorInviteeId(null);
          }}
          onClosePalette={() => setPaletteFor(null)}
          onCloseManager={() => setManagingTags(false)}
        />
      ) : null}
    </Stack>
  );
}
